import React, { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { useAuth } from '../context/AuthContext';
import { packerMyPacks, packerPackItems, packerSubmitPicks, packerSearchQueue } from '../api/predispatch';
import { getMobileReversalAppointments, mobileReversalAttempt } from '../api/dispatch';
import { allocatedInfo } from '../utils/allocated';
import PackerAudit from './PackerAudit';
import postcardUnder1 from '../assets/postcards/under_1.png';
import postcard1to2 from '../assets/postcards/1_2y.png';
import postcard2to4 from '../assets/postcards/2_4y.png';
import postcard4to6 from '../assets/postcards/4_6y.png';
import postcard6to8 from '../assets/postcards/6_8y.png';

// A pack's "postcards" (from the backend, one per distinct kid-age-band on
// the appointment) are rendered as extra found/not-found cards, same as any
// real item -- but they carry a "__postcard__" id and never reach the submit
// payload (packer_submit_picks would reject an id it doesn't recognise
// anyway). Nothing about what the packer picks here is ever stored.
const POSTCARD_IMAGES = {
  under_1: postcardUnder1,
  '1_2y': postcard1to2,
  '2_4y': postcard2to4,
  '4_6y': postcard4to6,
  '6_8y': postcard6to8,
};

// Gender shorthand for the compact item cards: Boy / Girl / Unisex -> B/G/U.
function genderShort(g) {
  const k = (g || '').toLowerCase();
  if (k === 'boy') return 'B';
  if (k === 'girl') return 'G';
  if (k === 'unisex') return 'U';
  return g || '';
}

// Self-contained packer interface. Packers (access_level='packer') see ONLY
// this — no other ZOCS nav or screens. Pick a pack -> mark each item
// found/not-found -> submit.
export default function PackerApp() {
  const { auth, logout } = useAuth();
  const [view, setView] = useState('packs');       // 'packs' | 'search' | 'reversal' | 'audit'
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);      // selected pack card
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState({});  // item_id -> 'found' | 'not_found'
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [searchItems, setSearchItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(true);
  const [reversalAppts, setReversalAppts] = useState([]);
  const [reversalLoading, setReversalLoading] = useState(true);
  const [activeReversal, setActiveReversal] = useState(null);  // selected in-transit order
  const [reversalCount, setReversalCount] = useState('');
  const [reversalBusy, setReversalBusy] = useState(false);
  const [reversalError, setReversalError] = useState(null);
  const [reversalNotLogged, setReversalNotLogged] = useState(false);
  // Ironing regime: items whose reversal landed on their 6th/12th/... showing
  // are already marked in_ironing_queue by the backend. This screen is a
  // physical instruction + acknowledgement only — confirming writes nothing.
  const [ironingBatch, setIroningBatch] = useState(null);

  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);

  const loadPacks = useCallback(async () => {
    setLoading(true);
    try { setPacks(await packerMyPacks()); }
    catch (e) { /* leave empty */ }
    finally { setLoading(false); }
  }, []);

  const loadSearchQueue = useCallback(async () => {
    setSearchLoading(true);
    try { setSearchItems(await packerSearchQueue()); }
    catch (e) { /* leave empty */ }
    finally { setSearchLoading(false); }
  }, []);

  const loadReversalAppts = useCallback(async () => {
    setReversalLoading(true);
    try { setReversalAppts(await getMobileReversalAppointments()); }
    catch (e) { /* leave empty */ }
    finally { setReversalLoading(false); }
  }, []);

  useEffect(() => { loadPacks(); }, [loadPacks]);
  useEffect(() => { if (view === 'search') loadSearchQueue(); }, [view, loadSearchQueue]);
  useEffect(() => { if (view === 'reversal') loadReversalAppts(); }, [view, loadReversalAppts]);

  function openReversal(appt) {
    setActiveReversal(appt); setReversalCount(''); setReversalError(null); setReversalNotLogged(false);
  }

  async function submitReversal() {
    if (!reversalCount) return;
    const count = parseInt(reversalCount, 10);
    if (isNaN(count) || count < 0) return;
    if (!window.confirm(`Submit a count of ${count}? You get one attempt — a wrong count locks this order and it can only be reversed from Order Ops after that.`)) return;
    setReversalBusy(true); setReversalError(null); setReversalNotLogged(false);
    try {
      const res = await mobileReversalAttempt(activeReversal.appointment_id, count);
      setActiveReversal(null); setReversalCount('');
      const toIron = res.ironing_items || [];
      const restockCount = res.match ? res.reversed_count - toIron.length : 0;
      setMsg(res.match
        ? { err: false, text: `Reversed — ${restockCount} item${restockCount !== 1 ? 's' : ''} assigned to you for restocking${toIron.length ? `, ${toIron.length} for ironing` : ''}.` }
        : { err: true, text: `Count didn't match. This order is now locked — it can only be reversed from Order Ops.` });
      if (toIron.length) setIroningBatch(toIron);
      loadReversalAppts();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.error === 'order_not_logged') {
        setReversalNotLogged(true);
      } else if (e.response?.status === 409 && detail?.error === 'mobile_reversal_locked') {
        setReversalError('This order was already attempted and is now locked. It can only be reversed from Order Ops.');
        setActiveReversal(null);
        loadReversalAppts();
      } else {
        setReversalError((detail && detail.message) || detail || 'Failed to submit.');
      }
    } finally { setReversalBusy(false); }
  }

  async function openPack(card) {
    setBusy(true); setMsg(null);
    try {
      const data = await packerPackItems(card.pack_id);
      const postcards = (data.postcards || []).map(band => ({
        item_id: `__postcard__${band}`, isPostcard: true, postcardImage: POSTCARD_IMAGES[band],
      }));
      setItems([...(data.items || []), ...postcards]);
      setResponses({});
      setActive(card);
    } catch (e) { setMsg({ err: true, text: e.response?.data?.detail || 'Could not open pack.' }); }
    finally { setBusy(false); }
  }

  function setPick(itemId, val) {
    setResponses(prev => ({ ...prev, [itemId]: val }));
  }

  const allAnswered = items.length > 0 && items.every(it => responses[it.item_id]);

  async function submit() {
    if (!allAnswered) return;
    setBusy(true); setMsg(null);
    try {
      const payload = items.filter(it => !it.isPostcard)
        .map(it => ({ item_id: it.item_id, pick_status: responses[it.item_id] }));
      const res = await packerSubmitPicks(active.pack_id, payload);
      setActive(null); setItems([]); setResponses({});
      setMsg({ err: false, text: res.not_found_count > 0
        ? `Submitted — ${res.not_found_count} not found, flagged for review.`
        : 'Submitted — all items found.' });
      loadPacks();
    } catch (e) { setMsg({ err: true, text: e.response?.data?.detail || 'Submit failed.' }); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-zoddle-green font-black text-lg">ZOCS</h1>
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full">Packer</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-700 text-sm font-medium">{auth.full_name}</span>
          <button onClick={logout} className="text-sm text-gray-400 hover:text-zoddle-pink">Sign out</button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto p-5">
        {msg && (
          <div className={`rounded-lg px-4 py-2 mb-4 text-sm font-medium ${msg.err ? 'bg-pink-50 text-zoddle-pink border border-pink-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {msg.text}
          </div>
        )}

        {!active && !activeReversal && !ironingBatch && (
          <div className="flex gap-2 mb-5">
            <button onClick={() => setView('packs')}
              className={`flex-1 text-sm font-semibold py-2 rounded-lg border transition ${view === 'packs' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
              My Packs
            </button>
            <button onClick={() => setView('search')}
              className={`flex-1 text-sm font-semibold py-2 rounded-lg border transition ${view === 'search' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
              Search Queue{searchItems.length > 0 ? ` (${searchItems.length})` : ''}
            </button>
            <button onClick={() => setView('reversal')}
              className={`flex-1 text-sm font-semibold py-2 rounded-lg border transition ${view === 'reversal' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
              Reversal
            </button>
            <button onClick={() => setView('audit')}
              className={`flex-1 text-sm font-semibold py-2 rounded-lg border transition ${view === 'audit' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>
              Audit
            </button>
          </div>
        )}

        {!active && !activeReversal && !ironingBatch && view === 'audit' && (
          <PackerAudit onMsg={setMsg} />
        )}

        {!active && !activeReversal && !ironingBatch && view === 'packs' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">My packs</h2>
            <p className="text-sm text-gray-400 mb-4">Packs assigned to you, awaiting picking.</p>
            {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
            : packs.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No packs assigned to you right now.</div>
            : (
              <div className="space-y-2">
                {packs.map(p => (
                  <button key={p.pack_id} onClick={() => openPack(p)} disabled={busy}
                    className="w-full text-left bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-zoddle-green transition flex items-center justify-between disabled:opacity-50">
                    <div>
                      <p className="font-semibold text-gray-800">{p.customer_name || '—'}</p>
                      <p className="text-xs text-gray-400">{p.scheduled_date} {p.scheduled_time || ''} · UID <span className="font-mono">{p.zcp_appointment_uid || '—'}</span></p>
                      {(() => { const a = allocatedInfo(p.allocated_at, now); return a ? <p className={`text-xs mt-0.5 ${a.cls}`}>{a.text}</p> : null; })()}
                    </div>
                    <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{p.item_count} items</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {!active && !activeReversal && !ironingBatch && view === 'search' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Search queue</h2>
            <p className="text-sm text-gray-400 mb-4">Items allocated to you to locate. A core team member marks these resolved once found.</p>
            {searchLoading ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
            : searchItems.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">Nothing allocated to you right now.</div>
            : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {searchItems.map(it => (
                  <div key={it.item_id} className="border-2 border-gray-200 rounded-xl p-2">
                    {it.image_url_1
                      ? <img src={cldThumb(it.image_url_1, 300)} alt={it.design_name} className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                      : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                    <p className="text-xs truncate"><span className="font-semibold text-gray-800">{it.brand_name || '—'}</span><span className="text-gray-500"> · {it.size}</span></p>
                    <p className="text-xs text-gray-400 truncate"><span className="font-mono">{it.zoddle_barcode}</span> · <span className="font-bold text-zoddle-green">₹{it.zoddle_price}</span></p>
                    <p className="text-xs text-gray-400 mt-1">In search from {it.in_search_since?.slice(0,10) || '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!active && !activeReversal && !ironingBatch && view === 'reversal' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Reversal</h2>
            <p className="text-sm text-gray-400 mb-4">Orders in transit. You get one count attempt per order — a wrong count locks it to the standard Order Ops reversal flow.</p>
            {reversalLoading ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
            : reversalAppts.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">Nothing in transit right now.</div>
            : (
              <div className="space-y-2">
                {reversalAppts.map(a => (
                  <div key={a.appointment_id}
                    className={`bg-white border rounded-xl px-4 py-3 flex items-center justify-between ${a.mobile_reversal_locked ? 'border-gray-200 opacity-60' : 'border-gray-200'}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{a.customer_name || '—'}</p>
                      <p className="text-xs text-gray-400">{a.scheduled_date} {a.scheduled_time || ''} · UID <span className="font-mono">{a.zcp_appointment_uid || '—'}</span></p>
                    </div>
                    {a.mobile_reversal_locked
                      ? <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full shrink-0">Locked</span>
                      : <button onClick={() => openReversal(a)}
                          className="bg-zoddle-green text-white text-sm font-bold px-5 py-2 rounded-lg shrink-0">Reverse</button>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {ironingBatch && (
          <>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
              <h2 className="text-lg font-bold text-orange-800 mb-1">
                Move {ironingBatch.length} item{ironingBatch.length !== 1 ? 's' : ''} to the ironing rack
              </h2>
              <p className="text-xs text-orange-700">
                These have been out enough times to need ironing before they go back
                in a bin. Put them on the ironing rack — not in the restocking tray.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-24">
              {ironingBatch.map(it => (
                <div key={it.item_id} className="border border-orange-200 rounded-xl overflow-hidden bg-white">
                  {it.image_url_1
                    ? <img src={cldThumb(it.image_url_1, 300)} alt={it.design_name || ''}
                        className="w-full h-32 object-cover" />
                    : <div className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-300 text-xs">no image</div>}
                  <div className="p-2">
                    <p className="text-xs font-bold text-gray-800 truncate">{it.brand_name || '—'}</p>
                    <p className="text-[11px] text-gray-500 truncate">{it.design_name || '—'}</p>
                    <p className="text-[11px] font-mono text-gray-600 mt-1">{it.zoddle_barcode}</p>
                    <p className="text-[11px] text-gray-400">
                      {it.size || '—'} · shown {it.showing_number}&times;
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
              <div className="max-w-3xl mx-auto">
                <button onClick={() => setIroningBatch(null)}
                  className="w-full bg-orange-600 text-white text-sm font-bold py-3 rounded-lg">
                  I've put {ironingBatch.length !== 1 ? 'them' : 'it'} on the ironing rack
                </button>
              </div>
            </div>
          </>
        )}

        {activeReversal && (
          <>
            <button onClick={() => { setActiveReversal(null); setReversalCount(''); setReversalError(null); setReversalNotLogged(false); }}
              className="text-sm text-gray-400 hover:text-gray-600 mb-3">← Back to reversal</button>
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-800">{activeReversal.customer_name || '—'}</h2>
              <p className="text-xs text-gray-400">UID <span className="font-mono">{activeReversal.zcp_appointment_uid || '—'}</span></p>
            </div>

            {reversalError && <div className="bg-pink-50 border border-pink-200 text-zoddle-pink text-sm rounded-lg px-4 py-2 mb-4">{reversalError}</div>}

            {reversalNotLogged ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-yellow-800 mb-1">Order not logged.</p>
                <p className="text-xs text-yellow-700 mb-3">This order hasn't been logged as sold, RTO, or DOL yet. It doesn't count as your attempt — try again once it's logged.</p>
                <button onClick={submitReversal} disabled={reversalBusy}
                  className="w-full bg-yellow-600 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50">
                  {reversalBusy ? 'Checking…' : 'Retry'}
                </button>
              </div>
            ) : (
              <>
                <label className="block text-sm font-semibold text-gray-700 mb-2">How many pieces are in the packet?</label>
                <p className="text-xs text-zoddle-pink font-semibold mb-2">Confirm handover from rider.</p>
                <input type="number" inputMode="numeric" min="0" value={reversalCount}
                  onChange={e => setReversalCount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg font-bold text-center mb-4"
                  placeholder="0" autoFocus />
                <button onClick={submitReversal} disabled={reversalBusy || !reversalCount}
                  className="w-full bg-zoddle-green text-white text-sm font-bold py-3 rounded-lg disabled:opacity-50">
                  {reversalBusy ? 'Submitting…' : 'Submit count'}
                </button>
              </>
            )}
          </>
        )}

        {active && (
          <>
            <button onClick={() => { setActive(null); setItems([]); setResponses({}); }}
              className="text-sm text-gray-400 hover:text-gray-600 mb-3">← Back to my packs</button>
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{active.customer_name}</h2>
                <p className="text-xs text-gray-400">UID <span className="font-mono">{active.zcp_appointment_uid || '—'}</span> · {items.length} items</p>
              </div>
              <span className="text-sm font-semibold text-gray-500">{Object.keys(responses).length}/{items.length} answered</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-24">
              {items.map(it => {
                const pick = responses[it.item_id];
                return (
                  <div key={it.item_id} className={`border-2 rounded-xl p-2 transition ${pick === 'found' ? 'border-zoddle-green' : pick === 'not_found' ? 'border-zoddle-pink' : 'border-gray-200'}`}>
                    {it.isPostcard
                      ? <img src={it.postcardImage} alt="Postcard gift" className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                      : it.image_url_1
                      ? <img src={cldThumb(it.image_url_1, 300)} alt={it.design_name} className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                      : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                    {it.isPostcard
                      ? <p className="text-xs font-semibold text-gray-800 mb-2">Postcard gift — include in pack</p>
                      : <>
                          <p className="text-xs truncate"><span className="font-semibold text-gray-800">{it.brand_name || '—'}</span><span className="text-gray-500"> · {it.size}{it.gender ? ` · ${genderShort(it.gender)}` : ''}</span></p>
                          <p className="text-xs text-gray-400 truncate mb-2"><span className="font-mono">{it.zoddle_barcode}</span> · <span className="font-bold text-zoddle-green">₹{it.zoddle_price}</span></p>
                        </>}
                    <div className="grid grid-cols-2 gap-1.5">
                      <button onClick={() => setPick(it.item_id, 'found')}
                        className={`text-xs font-semibold py-1.5 rounded-lg border transition ${pick === 'found' ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500 hover:border-zoddle-green'}`}>Found</button>
                      <button onClick={() => setPick(it.item_id, 'not_found')}
                        className={`text-xs font-semibold py-1.5 rounded-lg border transition ${pick === 'not_found' ? 'bg-zoddle-pink text-white border-zoddle-pink' : 'border-gray-200 text-gray-500 hover:border-zoddle-pink'}`}>Not found</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-5 py-3">
              <div className="max-w-3xl mx-auto">
                <button onClick={submit} disabled={!allAnswered || busy}
                  className="w-full bg-zoddle-green text-white text-sm font-semibold py-3 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                  {busy ? 'Submitting…' : allAnswered ? 'Submit' : `Answer all ${items.length} items to submit`}
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
